import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Upload, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ApplyVolunteer() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    social_media_handle: '',
    worked_conventions_before: 'no',
    previous_experience: '',
    favorite_anime: '',
    favorite_anime_reason: '',
    why_great_fit: '',
    poc_representation_importance: '',
    additional_details: '',
    video_url: '',
    agree_no_show_forfeit: false,
    agree_follow_directives: false,
    agree_volunteer_terms: false,
    agree_misconduct_dismissal: false,
  });

  useEffect(() => {
    fetchEvent();
  }, [eventId]);

  const fetchEvent = async () => {
    try {
      // Use public endpoint - no auth required
      const response = await axios.get(`${BACKEND_URL}/api/events/${eventId}/public`);
      setEvent(response.data);
    } catch (error) {
      console.error('Event not found');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.agree_no_show_forfeit || !formData.agree_follow_directives || 
        !formData.agree_volunteer_terms || !formData.agree_misconduct_dismissal) {
      toast.error('Please agree to all terms and conditions');
      return;
    }

    setLoading(true);

    try {
      const params = new URLSearchParams({
        event_id: eventId,
        event_name: event?.name || 'Unknown Event',
        application_type: 'volunteer',
        ...formData
      });
      
      await axios.post(`${BACKEND_URL}/api/staff-applications?${params.toString()}`);
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit application');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="max-w-2xl w-full bg-card/40 backdrop-blur-xl border border-border/50">
          <CardContent className="p-12 text-center">
            <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-6" />
            <h1 className="text-4xl font-bold font-heading mb-4">Application Submitted!</h1>
            <p className="text-lg text-muted-foreground mb-4">
              Thank you for applying to work with the Vyrus Team at <span className="text-primary font-semibold">{event?.name}</span>!
            </p>
            <p className="text-sm text-muted-foreground">
              We will reach out if there is a fit. If not, no hard feelings - keep applying!
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold font-heading text-primary mb-4">
            Volunteer Staff Application
          </h1>
          <p className="text-xl text-muted-foreground">
            {event?.name || 'Loading...'}
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            {event?.location} | {event?.start_date}
          </p>
        </div>

        <Card className="bg-yellow-500/10 border-yellow-500/50 mb-6">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-6 w-6 text-yellow-400 flex-shrink-0 mt-1" />
              <div className="text-sm text-yellow-400">
                <p className="font-semibold mb-2">Important Notice:</p>
                <p>Thank you for your interest in working with the Vyrus Team! We intensely vet every applicant and will reach out if there is a fit. There is no need to reach out for a follow-up to be considered. If we don't contact you, no hard feelings - continue to apply for future events!</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card/40 backdrop-blur-xl border border-border/50">
          <CardHeader>
            <CardTitle className="text-2xl font-heading">Application Form</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Contact Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-primary">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="full_name">Full Name *</Label>
                    <Input
                      id="full_name"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      required
                      className="bg-secondary/50 border-transparent focus:border-primary"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-secondary/50 border-transparent focus:border-primary"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="bg-secondary/50 border-transparent focus:border-primary"
                    />
                  </div>
                  <div>
                    <Label htmlFor="social_media_handle">Social Media Handle *</Label>
                    <Input
                      id="social_media_handle"
                      name="social_media_handle"
                      placeholder="@username"
                      value={formData.social_media_handle}
                      onChange={handleChange}
                      required
                      className="bg-secondary/50 border-transparent focus:border-primary"
                    />
                  </div>
                </div>
              </div>

              {/* Experience */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-primary">Experience</h3>
                <div>
                  <Label>Have you worked sales at a convention before? *</Label>
                  <select
                    name="worked_conventions_before"
                    value={formData.worked_conventions_before}
                    onChange={handleChange}
                    className="w-full mt-2 px-3 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground focus:outline-none focus:ring-1 focus:ring-primary h-11"
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>
                {formData.worked_conventions_before === 'yes' && (
                  <div>
                    <Label htmlFor="previous_experience">Please provide details (when, where, for what company) *</Label>
                    <Textarea
                      id="previous_experience"
                      name="previous_experience"
                      value={formData.previous_experience}
                      onChange={handleChange}
                      required={formData.worked_conventions_before === 'yes'}
                      rows={3}
                      className="bg-secondary/50 border-transparent focus:border-primary"
                    />
                  </div>
                )}
              </div>

              {/* Questions */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-primary">About You</h3>
                <div>
                  <Label htmlFor="favorite_anime">What is your favorite anime show? *</Label>
                  <Input
                    id="favorite_anime"
                    name="favorite_anime"
                    value={formData.favorite_anime}
                    onChange={handleChange}
                    required
                    className="bg-secondary/50 border-transparent focus:border-primary"
                  />
                </div>
                <div>
                  <Label htmlFor="favorite_anime_reason">Why is it your favorite? *</Label>
                  <Textarea
                    id="favorite_anime_reason"
                    name="favorite_anime_reason"
                    value={formData.favorite_anime_reason}
                    onChange={handleChange}
                    required
                    rows={3}
                    className="bg-secondary/50 border-transparent focus:border-primary"
                  />
                </div>
                <div>
                  <Label htmlFor="why_great_fit">Why do you feel you're a great fit at this convention for the Vyrus team? *</Label>
                  <Textarea
                    id="why_great_fit"
                    name="why_great_fit"
                    value={formData.why_great_fit}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="bg-secondary/50 border-transparent focus:border-primary"
                  />
                </div>
                <div>
                  <Label htmlFor="poc_representation_importance">What is most important in your opinion about the Black/POC community being represented at conventions or in the anime/comic space? *</Label>
                  <Textarea
                    id="poc_representation_importance"
                    name="poc_representation_importance"
                    value={formData.poc_representation_importance}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="bg-secondary/50 border-transparent focus:border-primary"
                  />
                </div>
                <div>
                  <Label htmlFor="additional_details">What other details do you want us to know about you?</Label>
                  <Textarea
                    id="additional_details"
                    name="additional_details"
                    value={formData.additional_details}
                    onChange={handleChange}
                    rows={3}
                    className="bg-secondary/50 border-transparent focus:border-primary"
                  />
                </div>
              </div>

              {/* Video Upload */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-primary">Video Introduction</h3>
                <div>
                  <Label htmlFor="video_url">Upload a 30-second video discussing why you're a great fit *</Label>
                  <Input
                    id="video_url"
                    name="video_url"
                    placeholder="Paste YouTube, Vimeo, or Google Drive link"
                    value={formData.video_url}
                    onChange={handleChange}
                    required
                    className="bg-secondary/50 border-transparent focus:border-primary mt-2"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Upload your video to YouTube (unlisted), Vimeo, or Google Drive and paste the link here
                  </p>
                </div>
              </div>

              {/* Terms and Conditions */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-primary">Terms & Acknowledgements</h3>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <input
                      id="agree_no_show_forfeit"
                      name="agree_no_show_forfeit"
                      type="checkbox"
                      checked={formData.agree_no_show_forfeit}
                      onChange={handleChange}
                      required
                      className="mt-1 h-4 w-4 rounded border-border bg-secondary text-primary focus:ring-primary"
                    />
                    <Label htmlFor="agree_no_show_forfeit" className="cursor-pointer text-sm">
                      I agree that failure to show up for any day I have committed to, regardless of the situation (emergency or not), will forfeit the position immediately.
                    </Label>
                  </div>
                  <div className="flex items-start space-x-3">
                    <input
                      id="agree_follow_directives"
                      name="agree_follow_directives"
                      type="checkbox"
                      checked={formData.agree_follow_directives}
                      onChange={handleChange}
                      required
                      className="mt-1 h-4 w-4 rounded border-border bg-secondary text-primary focus:ring-primary"
                    />
                    <Label htmlFor="agree_follow_directives" className="cursor-pointer text-sm">
                      I agree that management directives will be followed and in exchange multiple breaks will be assigned to ensure the convention can be enjoyed.
                    </Label>
                  </div>
                  <div className="flex items-start space-x-3">
                    <input
                      id="agree_volunteer_terms"
                      name="agree_volunteer_terms"
                      type="checkbox"
                      checked={formData.agree_volunteer_terms}
                      onChange={handleChange}
                      required
                      className="mt-1 h-4 w-4 rounded border-border bg-secondary text-primary focus:ring-primary"
                    />
                    <Label htmlFor="agree_volunteer_terms" className="cursor-pointer text-sm">
                      I agree that I will not be paid a traditional monetary payment, but instead I will be provided with a badge for free entry to the convention, one snack/meal, and multiple breaks throughout the day(s) I work to explore the convention. Failure to return on time from break could result in being dismissed and being unable to work with the Vyrus Team again.
                    </Label>
                  </div>
                  <div className="flex items-start space-x-3">
                    <input
                      id="agree_misconduct_dismissal"
                      name="agree_misconduct_dismissal"
                      type="checkbox"
                      checked={formData.agree_misconduct_dismissal}
                      onChange={handleChange}
                      required
                      className="mt-1 h-4 w-4 rounded border-border bg-secondary text-primary focus:ring-primary"
                    />
                    <Label htmlFor="agree_misconduct_dismissal" className="cursor-pointer text-sm">
                      I agree that if I cause any issue deemed as damaging to the brand, inappropriate, etc., I will be dismissed immediately, escorted out of the show, banned from working with the Vyrus team in the future, and not compensated for any hours worked.
                    </Label>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-medium text-lg"
              >
                {loading ? 'Submitting...' : 'Submit Volunteer Application'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
